import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { TRIGGER_LABEL, formatJobTime, formatBytes } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestorePoint = components['schemas']['RestorePoint']

/**
 * `v-snapshots` — process/09-SCREEN-SPECS/v-snapshots.md. The full-screen twin of `m-rpoint`'s
 * table (same operation, `kind=DATA_BACKUP`) — this one paginates by cursor and its per-row
 * commit is a navigation into a restore wizard (`Restore from this`), not a selection callback.
 * **A data restore point does not itself say which of §18.3's eight kinds an operator wants** —
 * this always opens `repo-wz-1` (the repository-restore entry, the common case); the wizard's own
 * first pane is where the kind is actually chosen. Reported as a judgment call, not a spec
 * requirement. `Export CSV` is drawn against no operation at all (`D89`) — the button is rendered,
 * disabled, and wired to nothing, exactly as the spec requires.
 *
 * **`panel`/`grid-filter-row` added W166** — the table wrapped in a real `panel`; a real "Include
 * expired restore points" filter using `jobListRestorePoints`'s own already-real `include_expired`
 * parameter (previously hardcoded `true`), matching the identical fix `SelectRestorePointPane.tsx`
 * got at W162.
 *
 * **`point.repositories` guarded, not trusted (found live, 2026-09-25) — same fix and same root
 * cause as `MRpoint.tsx`'s own doc comment**: `list_restore_points.py`'s real `RestorePoint(...)`
 * construction never sets `repositories` despite it being `required`, and this table crashed on it
 * exactly the way the modal did before its own fix.
 */
export function VSnapshots() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const pager = useCursorPager()
  const [includeExpired, setIncludeExpired] = useState(true)

  const list = useQuery({
    queryKey: ['restore-points', orgId, workspaceId, pager.cursor, includeExpired],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { kind: 'DATA_BACKUP', include_expired: includeExpired, cursor: pager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const problem = list.error instanceof ApiProblemError ? list.error.problem : undefined
  const points = (list.data?.items ?? []) as unknown as readonly RestorePoint[]

  return (
    <div data-scr={unitId('v-snapshots')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Restore points</h1>
        <div data-cmp="grid-tools">
          <Button data-act={undefined} ghost disabled title="No export exists for this list (D89)">
            Export CSV
          </Button>
          <Button
            data-act={undefined}
            ghost
            onClick={() => {
              pager.reset()
              setIncludeExpired(true)
            }}
          >
            Reset filters
          </Button>
        </div>
      </div>

      <p className="text-body-lg text-tx2 mb-4">
        Every backup that can still be restored from, newest first. A restore point is a backup that exists — there
        is no way to ask for an arbitrary time, because a time we never captured is not something we could put back.
        Each row pairs the data snapshot with the newest configuration snapshot taken at or before it; that pairing
        is what a restore actually uses.
      </p>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
        <div data-cmp="grid-filter-row" className="px-4 py-3 border-b border-bd">
          <label className="flex items-center gap-2 text-small text-tx2">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => {
                pager.reset()
                setIncludeExpired(e.target.checked)
              }}
            />
            Include expired restore points
          </label>
        </div>
        <GridTable
          columns={[
            {
              key: 'dataSnapshot',
              header: 'Data snapshot',
              render: (point) => (
                <span className="font-mono">
                  {point.started_at ? point.started_at.display : formatJobTime(point.data_snapshot.started_at)}
                  {point.repositories && point.repositories.numerator < point.repositories.denominator && (
                    <Note>{point.repositories.denominator - point.repositories.numerator} repositories not reached — a real restore point for the rest.</Note>
                  )}
                  {point.expired && point.expires_at && <Note>Retention expired {formatJobTime(point.expires_at)}. The stored objects were not deleted.</Note>}
                </span>
              ),
            },
            { key: 'trigger', header: 'Trigger', render: (point) => <Chip>{TRIGGER_LABEL[point.data_snapshot.trigger] ?? point.data_snapshot.trigger}</Chip> },
            { key: 'repositories', header: 'Repositories', render: (point) => point.repositories?.caption ?? '—' },
            { key: 'returns', header: 'Returns', render: (point) => (point.content_bytes !== undefined ? formatBytes(point.content_bytes) : '—') },
            {
              key: 'pairedConfig',
              header: 'Paired configuration snapshot',
              render: (point) =>
                point.configuration_snapshot ? (
                  `${formatJobTime(point.configuration_snapshot.started_at)} · ${TRIGGER_LABEL[point.configuration_snapshot.trigger] ?? point.configuration_snapshot.trigger}`
                ) : (
                  <span className="text-muted">No configuration captured at or before this point.</span>
                ),
            },
            { key: 'restorableUntil', header: 'Restorable until', render: (point) => (point.expires_at ? formatJobTime(point.expires_at) : '—') },
            {
              key: 'action',
              header: '',
              render: (point) => (
                <Button data-act={actionId('nav.repo-wz-1')} ghost disabled={point.expired || point.selectable === false} onClick={() => navigate('/repo-wz-1')}>
                  Restore from this
                </Button>
              ),
            },
          ]}
          rows={points}
          getRowKey={(point) => point.data_snapshot.job_id}
          rowClassName={(point) => (point.expired ? 'bg-row' : '')}
          emptyMessage={list.isLoading || problem ? '' : 'No restore points match these filters.'}
        />

        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>
            Restore points are listed newest first. An expired one is listed and not selectable; a partial one is
            selectable and names what it did not reach. Paged by cursor only — no total is served for this list.
          </Note>
          <Pager
            hasPrev={pager.hasPrev}
            hasNext={Boolean(list.data?.next_cursor)}
            loading={list.isFetching}
            onPrev={pager.goPrev}
            onNext={() => {
              if (list.data?.next_cursor) pager.goNext(list.data.next_cursor)
            }}
          />
        </div>
      </div>

      <div className="mt-4">
        <Strip>
          Why some rows read as warnings rather than being hidden. A partial backup is still a restore point for
          everything it did reach, and a snapshot with no configuration half is still a restore point for code and
          history. Hiding either would deny you a recovery you actually have. What is never done is inferring the
          missing half — a configuration snapshot that was not taken is shown as none captured, never as a workspace
          that had no settings.
        </Strip>
      </div>
      <div className="mt-4">
        <Strip>
          What retention does, and does not do. Retention is stamped on a backup when it is created, from the period
          the workspace has chosen — one year, three years, or seven on Enterprise. When it expires the backup stops
          being listed and stops being restorable — but the stored objects are not deleted, because a later backup
          may still point at them. So an expired row is a boundary on what you can recover, not a deletion.
        </Strip>
      </div>
    </div>
  )
}
